import { useEffect } from 'react';

import { selectEntriesForHunt, useHuntJournalStore } from '@/stores/hunt-journal-store';
import { JournalComposer } from './JournalComposer';
import { JournalEntryCard } from './JournalEntryCard';

interface JournalFeedProps {
  huntId: string;
}

export function JournalFeed({ huntId }: JournalFeedProps) {
  const loadEntries = useHuntJournalStore((s) => s.loadEntries);
  // Select the raw map (stable reference) and derive the per-hunt array via a
  // helper that returns a stable empty array when no entries exist for huntId.
  // Doing `s.entriesByHuntId[huntId] ?? []` directly inside the selector
  // returns a new `[]` every render — Zustand sees the reference inequality
  // and loops indefinitely (React error #185). See selectEntriesForHunt docs.
  const entriesByHuntId = useHuntJournalStore((s) => s.entriesByHuntId);
  const entries = selectEntriesForHunt(entriesByHuntId, huntId);

  useEffect(() => {
    void loadEntries(huntId);
  }, [huntId, loadEntries]);

  return (
    <div className="flex flex-col gap-4" data-testid="hunt-journal-feed">
      {/* Section header */}
      <p
        className="field-label"
        data-testid="hunt-section-header-journal"
      >
        {`JOURNAL · ${entries.length}`}
      </p>

      {/* Inline composer — always visible */}
      <JournalComposer huntId={huntId} />

      {/* Entry list or empty state */}
      {entries.length === 0 ? (
        <div
          data-testid="journal-empty-state"
          className="text-center py-6 text-body-sm text-[var(--color-text-muted)]"
        >
          No entries yet. Capture a moment from the field.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {entries.map((entry) => (
            <JournalEntryCard key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}
